import { statGap, sortStatsByRelativeGap } from './stats.js';
import { JOB_LEVEL_PACE, JOB_LEVEL_PACE_DECAY, JOB_LEVEL_TUNING, PLAYER_LEVEL_TUNING, XP_REWARDS } from './tuning.js';
import type { ActionType, Quest, Stat, StatVector } from './types.js';

/**
 * クエストテンプレート (03-game-design.md §クエストテンプレートプール)。
 */
export interface QuestTemplate {
  id: string;
  type: 'growth' | 'maintenance' | 'restraint';
  targetStat: Stat;
  descriptionTemplate: string; // {N} を required count で置換
  requiredCountFn: (level: number) => number;
  xpRewardFn: (count: number) => number;
  /** restraint で禁止される行動タイプ。この行動をすると失敗扱い。 */
  forbiddenActionTypes?: ActionType[];
  /** growth / maintenance がカウントの対象とする行動タイプ。これらの行動で currentCount が +1。 */
  expectedActionTypes?: ActionType[];
}

/**
 * クエストテンプレートプール。
 * 5 軸 × growth + maintenance + restraint。
 *
 * action-classifier (5 カテゴリ) + structural detection (引用 / 返信 / 文字数 /
 * 自スレ継続) の合成で確定的にカウントできるテンプレのみに絞っている。
 * `streak_maintain` `repost_only` `like_underseen` `quick_reply` (parent timestamp 必要)
 * は現在 pipeline に入っていないため、それらに依存する templates は除外した。
 *
 * requiredCountFn は LV 補正を緩めに (1 LV ごと +10%)。restraint は 0 回、maintenance は 1 回。
 */
const scaleLv = (base: number) => (lv: number) => Math.max(base, Math.floor(base * (1 + (lv - 1) * 0.1)));

export const DEFAULT_QUEST_TEMPLATES: QuestTemplate[] = [
  // ─── ATK 成長 ───
  { id: 'atk_opinion_post',  type: 'growth', targetStat: 'atk', descriptionTemplate: '自分の意見を {N} 個発信せよ',         requiredCountFn: scaleLv(3), xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['opinion_post'] },
  { id: 'atk_debate_reply',  type: 'growth', targetStat: 'atk', descriptionTemplate: '自分の考えを {N} 回ぶつけよ',         requiredCountFn: scaleLv(2), xpRewardFn: n => 60 + n * 12, expectedActionTypes: ['opinion_post'] },
  { id: 'atk_quote_opinion', type: 'growth', targetStat: 'atk', descriptionTemplate: '誰かの投稿を引用し自分の立場を {N} 回示せ', requiredCountFn: scaleLv(2), xpRewardFn: n => 60 + n * 12, expectedActionTypes: ['opinion_post', 'quote_with_opinion'] },
  { id: 'atk_hot_take',      type: 'growth', targetStat: 'atk', descriptionTemplate: '躊躇せず思ったことを {N} 回ポストせよ', requiredCountFn: scaleLv(3), xpRewardFn: n => 55 + n * 10, expectedActionTypes: ['opinion_post', 'short_burst'] },

  // ─── DEF 成長 ───
  { id: 'def_thread',         type: 'growth', targetStat: 'def', descriptionTemplate: '自分のスレッドに {N} 件、自分で返信を重ねよ', requiredCountFn: () => 3,     xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['thread_continue', 'analysis_post'] },
  { id: 'def_steady_post',    type: 'growth', targetStat: 'def', descriptionTemplate: '今日のうちに {N} 投稿、地に足をつけよ', requiredCountFn: () => 3,     xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['short_burst', 'analysis_post', 'opinion_post'] },
  { id: 'def_calm_debate',    type: 'growth', targetStat: 'def', descriptionTemplate: '会話に {N} 回、落ち着いて長めに返答せよ', requiredCountFn: scaleLv(2), xpRewardFn: n => 65 + n * 13, expectedActionTypes: ['calm_debate_reply', 'analysis_post'] },
  { id: 'def_deep_reply',     type: 'growth', targetStat: 'def', descriptionTemplate: '会話を {N} 回、深めるように返せ',     requiredCountFn: scaleLv(2), xpRewardFn: n => 60 + n * 12, expectedActionTypes: ['analysis_post', 'calm_debate_reply'] },
  { id: 'def_anchored_series',type: 'growth', targetStat: 'def', descriptionTemplate: 'じっくり考えた投稿を {N} 件書け',     requiredCountFn: () => 3,     xpRewardFn: n => 70 + n * 15, expectedActionTypes: ['analysis_post'] },

  // ─── AGI 成長 ───
  { id: 'agi_short_post',    type: 'growth', targetStat: 'agi', descriptionTemplate: '軽やかに短文を {N} 個書け',           requiredCountFn: scaleLv(3), xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['short_burst'] },
  { id: 'agi_quick_reply',   type: 'growth', targetStat: 'agi', descriptionTemplate: '短文を {N} 件、テンポよく投稿せよ',   requiredCountFn: () => 3,     xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['short_burst'] },
  { id: 'agi_rhythm_chain',  type: 'growth', targetStat: 'agi', descriptionTemplate: '今日の瞬間を {N} 投稿、リズムを保て', requiredCountFn: scaleLv(3), xpRewardFn: n => 55 + n * 10, expectedActionTypes: ['short_burst'] },
  { id: 'agi_pulse_burst',   type: 'growth', targetStat: 'agi', descriptionTemplate: '思いつきを {N} 回切り取って投稿',     requiredCountFn: scaleLv(3), xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['short_burst'] },
  { id: 'agi_skim_reply',    type: 'growth', targetStat: 'agi', descriptionTemplate: '軽い反応を {N} 件記せ',               requiredCountFn: scaleLv(4), xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['short_burst'] },

  // ─── INT 成長 ───
  { id: 'int_long_post',      type: 'growth', targetStat: 'int', descriptionTemplate: '200 字以上の分析を {N} 件書け',        requiredCountFn: () => 2,     xpRewardFn: n => 70 + n * 15, expectedActionTypes: ['analysis_post'] },
  { id: 'int_quote_analysis', type: 'growth', targetStat: 'int', descriptionTemplate: '引用して考察を添えよ、{N} 回',         requiredCountFn: () => 2,     xpRewardFn: n => 60 + n * 12, expectedActionTypes: ['analysis_post', 'quote_with_analysis'] },
  { id: 'int_critical_read',  type: 'growth', targetStat: 'int', descriptionTemplate: '批判的な観点で {N} 件書け',           requiredCountFn: scaleLv(2), xpRewardFn: n => 70 + n * 14, expectedActionTypes: ['analysis_post', 'opinion_post'] },
  { id: 'int_research_reply', type: 'growth', targetStat: 'int', descriptionTemplate: '掘り下げた分析を {N} 件書け',         requiredCountFn: scaleLv(2), xpRewardFn: n => 65 + n * 13, expectedActionTypes: ['analysis_post'] },

  // ─── LUK 成長 ───
  { id: 'luk_empathy',       type: 'growth', targetStat: 'luk', descriptionTemplate: '{N} 人に共感を贈れ',                   requiredCountFn: scaleLv(5), xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['empathy_reply'] },
  { id: 'luk_warm_welcome',  type: 'growth', targetStat: 'luk', descriptionTemplate: '誰かに {N} 回、優しい声をかけよ',      requiredCountFn: scaleLv(2), xpRewardFn: n => 60 + n * 12, expectedActionTypes: ['empathy_reply'] },
  { id: 'luk_thank_reply',   type: 'growth', targetStat: 'luk', descriptionTemplate: '感謝の言葉を {N} 件記せ',               requiredCountFn: scaleLv(3), xpRewardFn: n => 50 + n * 10, expectedActionTypes: ['empathy_reply'] },
  { id: 'luk_humor_post',    type: 'growth', targetStat: 'luk', descriptionTemplate: '空気を和ませる投稿を {N} 件書け',       requiredCountFn: scaleLv(2), xpRewardFn: n => 55 + n * 12, expectedActionTypes: ['humor_post'] },

  // ─── 維持 ───
  { id: 'maintenance_daily_word',   type: 'maintenance', targetStat: 'def', descriptionTemplate: '今日も一言を記せ',                     requiredCountFn: () => 1, xpRewardFn: () => 20, expectedActionTypes: ['short_burst', 'opinion_post', 'analysis_post', 'humor_post', 'empathy_reply'] },
  { id: 'maintenance_one_thought',  type: 'maintenance', targetStat: 'atk', descriptionTemplate: '今日、思ったことを一度だけ書け',       requiredCountFn: () => 1, xpRewardFn: () => 20, expectedActionTypes: ['opinion_post', 'short_burst'] },
  { id: 'maintenance_gentle_scroll',type: 'maintenance', targetStat: 'agi', descriptionTemplate: '気になった投稿に反応を残せ',           requiredCountFn: () => 1, xpRewardFn: () => 20, expectedActionTypes: ['short_burst', 'empathy_reply'] },
  { id: 'maintenance_read_silent',  type: 'maintenance', targetStat: 'int', descriptionTemplate: '読むだけの時間を作り、考えを言葉にしない日', requiredCountFn: () => 0, xpRewardFn: () => 20 },
  { id: 'maintenance_thanks_round', type: 'maintenance', targetStat: 'luk', descriptionTemplate: '誰か一人に静かに「ありがとう」を送れ',   requiredCountFn: () => 1, xpRewardFn: () => 20, expectedActionTypes: ['empathy_reply'] },

  // ─── 節制 ───
  { id: 'restraint_opinion',        type: 'restraint', targetStat: 'atk', descriptionTemplate: '今日は意見投稿を控えよ',       requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['opinion_post', 'quote_with_opinion'] },
  { id: 'restraint_hot_take',       type: 'restraint', targetStat: 'atk', descriptionTemplate: '衝動的な発信を一日休め',       requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['opinion_post'] },
  { id: 'restraint_long_analysis',  type: 'restraint', targetStat: 'int', descriptionTemplate: '今日は長文分析を控えよ',       requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['analysis_post', 'quote_with_analysis'] },
  { id: 'restraint_quote_analysis', type: 'restraint', targetStat: 'int', descriptionTemplate: '引用考察を一日置いておけ',     requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['quote_with_analysis'] },
  { id: 'restraint_short_burst',    type: 'restraint', targetStat: 'agi', descriptionTemplate: '今日は短文投稿を控えよ',       requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['short_burst'] },
  { id: 'restraint_thread',         type: 'restraint', targetStat: 'def', descriptionTemplate: '連続スレッドは一日休め',       requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['thread_continue'] },
  { id: 'restraint_empathy',        type: 'restraint', targetStat: 'luk', descriptionTemplate: '共感的な投稿は今日だけ休め',   requiredCountFn: () => 0, xpRewardFn: () => 80, forbiddenActionTypes: ['empathy_reply'] },
];

/**
 * ハッシュ関数 (シード生成用、FNV-1a 32-bit)。
 * ユーザー DID + 日付 で決定的にクエストを選ぶ。
 */
export function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export interface QuestGenInput {
  userDid: string;
  dateStr: string; // YYYY-MM-DD
  level: number;
  currentStats: StatVector;
  targetStats: StatVector;
  recentTemplateIds: readonly string[]; // 過去 7 日に出したテンプレID
  templates?: readonly QuestTemplate[]; // 指定なしなら DEFAULT_QUEST_TEMPLATES
}

/**
 * 毎日のクエストを 3 つ生成。
 * - 上位 2 軸のギャップを埋める成長クエスト
 * - 過剰軸の節制クエスト
 */
export function generateDailyQuests(input: QuestGenInput): Quest[] {
  const tmpls = input.templates ?? DEFAULT_QUEST_TEMPLATES;
  const gap = statGap(input.currentStats, input.targetStats);
  // 相対ギャップ (|gap| / target) で並べる。target レンジが 7〜44 と幅広いので、
  // 絶対点数で比較すると小 target stat (例: 遊び人 int=10) の歪みを過小評価する。
  const sortedStats = sortStatsByRelativeGap(gap, input.targetStats);

  const growthStats: Stat[] = [];
  const restraintStat: Stat[] = [];
  for (const s of sortedStats) {
    if (gap[s] > 0 && growthStats.length < 2) growthStats.push(s);
    else if (gap[s] < 0 && restraintStat.length < 1) restraintStat.push(s);
  }

  const seed = hashStr(`${input.userDid}:${input.dateStr}`);
  const recentSet = new Set(input.recentTemplateIds);
  const quests: Quest[] = [];
  let slotIdx = 0;

  const pickFrom = (candidates: QuestTemplate[]): QuestTemplate | null => {
    const filtered = candidates.filter(t => !recentSet.has(t.id));
    const pool = filtered.length > 0 ? filtered : candidates;
    if (pool.length === 0) return null;
    return pool[(seed + slotIdx) % pool.length] ?? null;
  };

  for (const stat of growthStats) {
    const candidates = tmpls.filter(t => t.type === 'growth' && t.targetStat === stat);
    const tmpl = pickFrom(candidates);
    if (tmpl) {
      const count = tmpl.requiredCountFn(input.level);
      quests.push({
        id: `${input.dateStr}-${slotIdx}`,
        templateId: tmpl.id,
        type: tmpl.type,
        targetStat: tmpl.targetStat,
        description: tmpl.descriptionTemplate.replace('{N}', String(count)),
        requiredCount: count,
        currentCount: 0,
        xpReward: tmpl.xpRewardFn(count),
        issuedDate: input.dateStr,
      });
    }
    slotIdx++;
  }

  for (const stat of restraintStat) {
    const candidates = tmpls.filter(t => t.type === 'restraint' && t.targetStat === stat);
    const tmpl = pickFrom(candidates);
    if (tmpl) {
      const q: Quest = {
        id: `${input.dateStr}-${slotIdx}`,
        templateId: tmpl.id,
        type: tmpl.type,
        targetStat: tmpl.targetStat,
        description: tmpl.descriptionTemplate,
        requiredCount: 0,
        currentCount: 0,
        xpReward: tmpl.xpRewardFn(0),
        issuedDate: input.dateStr,
      };
      if (tmpl.forbiddenActionTypes && tmpl.forbiddenActionTypes.length > 0) {
        q.forbiddenActionTypes = tmpl.forbiddenActionTypes as NonNullable<Quest['forbiddenActionTypes']>;
      }
      quests.push(q);
    }
    slotIdx++;
  }

  return quests;
}

/** 1 日に発行されるデイリークエストの最大枠数 (`generateDailyQuests` の slot 数)。
 *  成長 2 (ギャップの大きい stat 2 つ) + 節制 1。 */
export const MAX_DAILY_QUEST_SLOTS = 3;

/**
 * **1 日のデイリークエストで得られる XP の上限**。
 *
 * XP 申告の上限クランプ (#534) がこれを含まないと、**クエストを完了した日の XP が
 * 毎日クランプで消える**。デイリークエストは 1 件 20〜135 XP あり、1 投稿で複数同時に
 * 完了しうるので、「投稿 1 回ぶんの上限」に収まらない。
 *
 * テンプレート表から導く (数値を書き写すと報酬を変えたときに片方だけ直して事故る)。
 */
export const MAX_DAILY_QUEST_XP: number = (() => {
  const per = DEFAULT_QUEST_TEMPLATES
    .map((t) => t.xpRewardFn(t.requiredCountFn(JOB_LEVEL_TUNING.maxLevel)))
    .sort((a, b) => b - a);
  return per.slice(0, MAX_DAILY_QUEST_SLOTS).reduce((a, b) => a + b, 0);
})();

/**
 * 現職 (archetype) の滞在 LV 用 XP 曲線。パラメータは tuning.JOB_LEVEL_TUNING。
 * threshold(n) = round(coef * (n - 1)^exp)、LV1 = 0。
 */
export const JOB_XP_CURVE: ReadonlyArray<readonly [level: number, threshold: number]> = buildXpCurve(
  JOB_LEVEL_TUNING.maxLevel,
  JOB_LEVEL_TUNING.coefficient,
  JOB_LEVEL_TUNING.exponent,
);

/**
 * **職ごとの XP 曲線** (#536)。基準曲線に `JOB_LEVEL_PACE[archetype]` を掛ける。
 * 強い職ほど倍率が大きい = 同じレベルに必要な XP が多い = 上がりにくい。
 * 未知の職 (将来追加) は 1.0 = 基準どおり。
 */
export function jobXpCurveFor(archetype: string): ReadonlyArray<readonly [level: number, threshold: number]> {
  const pace = JOB_LEVEL_PACE[archetype] ?? 1;
  if (pace === 1) return JOB_XP_CURVE;
  const maxLv = JOB_LEVEL_TUNING.maxLevel;
  return JOB_XP_CURVE.map(([lv, th]) => {
    // **職差は序盤に大きく、後半に収束する** (DQ3 準拠)。DQ3 の職業別必要経験値は
    // Lv10 で最大 2.5 倍の開きがあるのに、Lv50 では 1.46 倍まで縮まる。
    // 「序盤は職の個性が強く出て、終盤は皆が同じ地平に立つ」という形。
    // 定数倍だと後半も開いたままで、強い職がいつまでも追いつけない。
    const t = Math.min(1, (lv - 1) / (maxLv - 1));
    return [lv, Math.round(th * Math.pow(pace, 1 - t * JOB_LEVEL_PACE_DECAY))] as const;
  });
}

/**
 * 累計 XP から現職 LV を計算。
 *
 * **`archetype` は必須** (#536)。職ごとに曲線が違うので、渡し忘れると
 * 「サーバーが数えたレベルと UI に出るレベルが食い違う」という最悪の壊れ方をする。
 * 省略可能にすると必ずどこかで抜けるため、型で強制して全呼び出し元に配らせる。
 */
export function jobLevelFromXp(xp: number, archetype: string): number {
  return jobLevelFromXpFor(archetype, xp);
}

/** 累計 XP から現職 LV を計算 (**職ごとの曲線**。#536)。 */
export function jobLevelFromXpFor(archetype: string, xp: number): number {
  let lv = 1;
  for (const [l, threshold] of jobXpCurveFor(archetype)) {
    if (xp >= threshold) lv = l;
    else break;
  }
  return lv;
}

/** 現職 LV の UI 進捗バー用 (**職ごとの曲線**。#536)。 */
export function jobXpToNextLevelFor(archetype: string, xp: number): { level: number; current: number; next: number } {
  const curve = jobXpCurveFor(archetype);
  const level = jobLevelFromXpFor(archetype, xp);
  const idx = curve.findIndex((e) => e[0] === level);
  const curThreshold = idx >= 0 ? curve[idx]![1] : 0;
  const nextEntry = idx >= 0 && idx + 1 < curve.length ? curve[idx + 1] : undefined;
  if (!nextEntry) return { level, current: xp - curThreshold, next: 0 };
  return { level, current: xp - curThreshold, next: nextEntry[1] - curThreshold };
}

/**
 * 現職 LV の UI 進捗バー用。
 * - level: 現在 LV
 * - current: 現在 LV に入ってから積んだ XP
 * - next: 次 LV までに必要な XP (現 LV 内の分母)
 * LV 50 に到達後は next = 0 (打ち止め)
 */
export function jobXpToNextLevel(xp: number, archetype: string): { level: number; current: number; next: number } {
  return jobXpToNextLevelFor(archetype, xp);
}

/** 現職 LV 用の XP 加算定数 (後方互換の別名。実体は tuning.XP_REWARDS)。 */
export const JOB_XP_REWARDS = XP_REWARDS;

/**
 * 個人 (プレイヤー) LV 用 XP 曲線。パラメータは tuning.PLAYER_LEVEL_TUNING。
 * `playerLevelFromXp` の内部表だけに使う (個人 LV は戦闘力・UI から外れている。#507/#508)。
 */
const PLAYER_XP_CURVE: ReadonlyArray<readonly [level: number, threshold: number]> = buildXpCurve(
  PLAYER_LEVEL_TUNING.maxLevel,
  PLAYER_LEVEL_TUNING.coefficient,
  PLAYER_LEVEL_TUNING.exponent,
);

/** 共通: threshold(n) = round(coef * (n - 1)^exp)、LV1..maxLv。 */
function buildXpCurve(maxLv: number, coefficient: number, exponent: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let lv = 1; lv <= maxLv; lv++) {
    out.push([lv, Math.round(coefficient * Math.pow(lv - 1, exponent))]);
  }
  return out;
}

/** 累計 XP から個人 LV を計算。 */
export function playerLevelFromXp(xp: number): number {
  let lv = 1;
  for (const [l, threshold] of PLAYER_XP_CURVE) {
    if (xp >= threshold) lv = l;
    else break;
  }
  return lv;
}
